/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback } from 'react';
import { ref, onValue, set, update } from 'firebase/database';
import { database } from '../firebase';
import { aiChat } from '../lib/aiApi';

/**
 * Derive honest completion counts from the progress/{goalId}/tasks subtree.
 * Latest-record-wins per logical task, grouped by metadata.taskKey (falling back
 * to description), using metadata.createdAtMs || createdAt as the recency key.
 */
const deriveTaskCounts = (tasksNode) => {
  const records = Object.values(tasksNode || {});
  const latest = new Map();
  records.forEach((r) => {
    const key = r?.metadata?.taskKey || r?.description;
    if (key == null) return;
    const recency = r?.metadata?.createdAtMs || r?.createdAt || 0;
    const prev = latest.get(key);
    const prevRecency = prev ? (prev.metadata?.createdAtMs || prev.createdAt || 0) : -1;
    if (!prev || recency >= prevRecency) latest.set(key, r);
  });
  const groups = Array.from(latest.values());
  return {
    completedTasks: groups.filter((r) => r.completed === true).length,
    totalTasks: groups.length
  };
};

/**
 * Custom hook for tracking and calculating comprehensive daily progress
 * Uses the business plan as the stable foundation for all planning
 */
export const useProgressTracking = (user, userGoal, businessPlan) => {
  const [progressData, setProgressData] = useState({
    overallProgress: 0,
    completedTasks: 0,
    totalTasks: 0,
    streakDays: 0,
    timeSpent: 0,
    daysSinceStart: 1,
    dailyGoalsMet: false,
    weeklyProgress: 0,
    weeklyGoalsMet: false,
    currentQuarter: 1,
    quarterProgress: 0
  });
  
  const [dailyHistory, setDailyHistory] = useState({});
  const [stableFoundationPlan, setStableFoundationPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  // Create stable foundation plan from business plan (non-negotiable structure)
  const createStableFoundationPlan = useCallback((goal, businessPlan) => {
    console.log('createStableFoundationPlan - goal:', goal);
    console.log('createStableFoundationPlan - businessPlan:', businessPlan);
    console.log('createStableFoundationPlan - businessPlan.phases:', businessPlan?.phases);
    
    if (!goal || !businessPlan) return null;

    // Calculate timeline from goal
    const startDate = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000); // Started 3 days ago
    const targetDate = goal.targetDate ? new Date(goal.targetDate) : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);
    const totalDays = Math.max(30, Math.ceil((targetDate - startDate) / (1000 * 60 * 60 * 24)));

    // Extract phases from business plan as foundation
    const businessPhases = businessPlan.phases || [];
    const quartersCount = 4;
    const daysPerQuarter = Math.floor(totalDays / quartersCount);

    const foundationPlan = {
      // Central goal - immutable foundation
      centralGoal: {
        title: goal.goal,
        description: goal.description || businessPlan.description,
        totalDays: totalDays,
        startDate: startDate.toISOString().split('T')[0],
        targetDate: targetDate.toISOString().split('T')[0],
        overallTarget: businessPlan.overallTarget || `Complete ${goal.goal}`
      },
      
      // Business plan foundation - this is the stable structure
      businessFoundation: {
        phases: businessPhases,
        dailyTimeCommitment: businessPlan.dailyTimeCommitment || '4-6 hours',
        totalBudget: businessPlan.totalBudget || 'TBD',
        businessStrategy: businessPhases[0]?.businessStrategy || businessPlan.content
      },

      // Timeline structure built on business foundation
      timeline: {
        totalDuration: `${totalDays} days`,
        quartersCount: quartersCount,
        daysPerQuarter: daysPerQuarter,
        milestones: generateMilestonesFromBusinessPlan(businessPhases, totalDays)
      },

      // Quarters based on business plan phases
      quarters: generateQuartersFromBusinessPlan(businessPhases, startDate, totalDays, goal, businessPlan)
    };

    return foundationPlan;
  }, []);

  // Generate milestones from business plan phases
  const generateMilestonesFromBusinessPlan = (phases, totalDays) => {
    if (!phases || phases.length === 0) {
      return [
        { day: Math.floor(totalDays * 0.25), title: "Foundation Complete" },
        { day: Math.floor(totalDays * 0.5), title: "Core Development" },
        { day: Math.floor(totalDays * 0.75), title: "Testing & Refinement" },
        { day: totalDays, title: "Launch & Scale" }
      ];
    }

    // Use business plan phases to create milestones
    return phases.map((phase, index) => ({
      day: Math.floor(totalDays * ((index + 1) / phases.length)),
      title: phase.title || `Phase ${index + 1} Complete`,
      description: phase.subtitle || phase.focus,
      successCriteria: phase.successCriteria
    }));
  };

  // Generate quarters from business plan phases (stable foundation)
  const generateQuartersFromBusinessPlan = (phases, startDate, totalDays, goal, businessPlan) => {
    const quartersCount = 4;
    const daysPerQuarter = Math.floor(totalDays / quartersCount);
    
    return Array.from({ length: quartersCount }, (_, quarterIndex) => {
      const quarterStart = quarterIndex * daysPerQuarter;
      const quarterEnd = Math.min((quarterIndex + 1) * daysPerQuarter, totalDays);
      const weeksInQuarter = Math.ceil((quarterEnd - quarterStart) / 7);
      
      // Map business plan phases to quarters
      const phaseIndex = Math.floor((quarterIndex * phases.length) / quartersCount);
      const businessPhase = phases[phaseIndex] || phases[0] || createDefaultPhase(quarterIndex + 1, goal);

      return {
        id: quarterIndex + 1,
        title: `Q${quarterIndex + 1}: ${businessPhase.title || getDefaultQuarterTitle(quarterIndex + 1)}`,
        description: businessPhase.subtitle || businessPhase.focus || getDefaultQuarterDescription(quarterIndex + 1, goal),
        startDay: quarterStart + 1,
        endDay: quarterEnd,
        totalDays: quarterEnd - quarterStart,
        
        // Business plan foundation for this quarter
        businessPhase: businessPhase,
        objectives: extractObjectivesFromPhase(businessPhase, quarterIndex + 1),
        dailyCommitment: businessPhase.dailyCommitment || businessPlan.dailyTimeCommitment || '4-6 hours',
        weeklyTarget: businessPhase.weeklyTarget || `Complete Q${quarterIndex + 1} milestones`,
        specificGoals: businessPhase.specificGoals || [],
        measurableSkills: businessPhase.measurableSkills || [],
        weeklyMilestones: businessPhase.weeklyMilestones || [],
        
        // Weeks within quarter based on business foundation
        weeks: generateWeeksFromBusinessPhase(businessPhase, quarterStart, quarterEnd, weeksInQuarter, goal, businessPlan)
      };
    });
  };

  // Generate weeks from business phase (maintains consistency)
  const generateWeeksFromBusinessPhase = (businessPhase, quarterStart, quarterEnd, weeksInQuarter, goal, businessPlan) => {
    return Array.from({ length: weeksInQuarter }, (_, weekIndex) => {
      const weekStart = quarterStart + (weekIndex * 7);
      const weekEnd = Math.min(weekStart + 7, quarterEnd);
      
      // Use business phase weekly schedule if available
      const weeklySchedule = businessPhase.weeklySchedule || [];
      const weekFocus = weeklySchedule[weekIndex] || 
                      businessPhase.dailyRoutine?.skillPractice || 
                      `Week ${weekIndex + 1} focus on ${businessPhase.title || goal.goal}`;

      return {
        id: weekIndex + 1,
        quarterWeek: weekIndex + 1,
        globalWeek: Math.floor(weekStart / 7) + 1,
        title: `Week ${weekIndex + 1}`,
        focus: weekFocus,
        startDay: weekStart + 1,
        endDay: weekEnd,
        
        // Business foundation for this week
        businessFocus: weekFocus,
        dailyRoutine: businessPhase.dailyRoutine || createDefaultDailyRoutine(goal),
        weeklyMilestone: businessPhase.weeklyMilestones?.[weekIndex] || `Complete week ${weekIndex + 1} objectives`,
        
        // Days based on business phase daily routine
        days: generateDaysFromBusinessRoutine(
          weekStart, 
          weekEnd, 
          businessPhase, 
          weekIndex + 1, 
          goal, 
          businessPlan
        )
      };
    });
  };

  // Generate daily actions from business routine (specific and measurable)
  const generateDaysFromBusinessRoutine = (weekStart, weekEnd, businessPhase, weekNumber, goal, businessPlan) => {
    const dailyRoutine = businessPhase.dailyRoutine || createDefaultDailyRoutine(goal);
    const specificGoals = businessPhase.specificGoals || [];
    const measurableSkills = businessPhase.measurableSkills || [];
    
    return Array.from({ length: weekEnd - weekStart }, (_, dayIndex) => {
      const globalDay = weekStart + dayIndex + 1;
      const dayOfWeek = ((dayIndex) % 7) + 1; // 1-7 (Mon-Sun)
      const dayName = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][dayIndex % 7];
      
      // Create specific daily actions based on business phase
      const dailyActions = createSpecificDailyActions(
        dayOfWeek,
        dayName,
        businessPhase,
        specificGoals,
        measurableSkills,
        goal,
        weekNumber,
        globalDay
      );

      return {
        id: dayIndex + 1,
        globalDay: globalDay,
        date: new Date(stableFoundationPlan?.centralGoal?.startDate || Date.now()).getTime() + (globalDay - 1) * 24 * 60 * 60 * 1000,
        title: `Day ${globalDay}`,
        dayName: dayName,
        
        // Business-driven actions (specific and measurable)
        actions: dailyActions,
        businessRoutine: dailyRoutine,
        priority: calculatePriorityFromBusinessPhase(dayOfWeek, businessPhase, weekNumber),
        estimatedHours: businessPhase.dailyCommitment || businessPlan.dailyTimeCommitment || '4-6 hours',
        
        // Measurable outcomes
        measurableOutcomes: extractMeasurableOutcomes(businessPhase, dayOfWeek, weekNumber),
        skillPractice: dailyRoutine.skillPractice || `Practice core skills for ${goal.goal}`,
        assessmentCriteria: getAssessmentCriteria(businessPhase, dayOfWeek)
      };
    });
  };

  // Create specific daily actions based on business phase structure
  const createSpecificDailyActions = (dayOfWeek, dayName, businessPhase, specificGoals, measurableSkills, goal, weekNumber, globalDay) => {
    const dailyRoutine = businessPhase.dailyRoutine || {};
    const goalTitle = goal.goal;
    
    // Base action structure from business phase
    let primaryAction = '';
    let secondaryActions = [];

    // Use business phase daily routine structure
    if (dayOfWeek === 1) { // Monday - Planning & Setup
      primaryAction = dailyRoutine.warmUp || `Plan and set up for ${businessPhase.title || goalTitle} - Week ${weekNumber}`;
      secondaryActions = [
        `Review weekly milestone: ${businessPhase.weeklyMilestones?.[weekNumber - 1] || 'Week objectives'}`,
        `Organize resources for ${businessPhase.title || goalTitle}`
      ];
    } else if (dayOfWeek === 2 || dayOfWeek === 3 || dayOfWeek === 4) { // Tue-Thu - Core Work
      primaryAction = dailyRoutine.skillPractice || `Execute core work for ${businessPhase.title || goalTitle}`;
      
      // Add specific goals if available
      if (specificGoals.length > 0) {
        const goalIndex = (globalDay - 1) % specificGoals.length;
        primaryAction += ` - Focus: ${specificGoals[goalIndex]}`;
      }
      
      // Add measurable skill practice
      if (measurableSkills.length > 0) {
        const skillIndex = (dayOfWeek - 2) % measurableSkills.length;
        const skill = measurableSkills[skillIndex];
        secondaryActions.push(`Practice ${skill.skill} - Target: ${skill.week1Target || 'Improvement'}`);
      }
      
      secondaryActions.push(dailyRoutine.conditioning || `Build capabilities for ${goalTitle}`);
    } else if (dayOfWeek === 5) { // Friday - Review & Assessment
      primaryAction = dailyRoutine.coolDown || `Review and assess progress on ${businessPhase.title || goalTitle}`;
      secondaryActions = [
        `Evaluate weekly milestone completion`,
        `Document lessons learned for ${goalTitle}`,
        `Prepare for next week's focus`
      ];
    } else { // Weekend - Reflection & Preparation
      primaryAction = `Reflect on progress and prepare for upcoming week of ${businessPhase.title || goalTitle}`;
      secondaryActions = [
        `Strategic thinking about ${goalTitle}`,
        `Research and learning for next phase`
      ];
    }

    return {
      primary: primaryAction,
      secondary: secondaryActions,
      routine: dailyRoutine,
      timeCommitment: businessPhase.dailyCommitment || '4-6 hours'
    };
  };

  // Helper functions for business plan integration
  const createDefaultPhase = (quarter, goal) => ({
    title: `Quarter ${quarter} Focus`,
    subtitle: `Development phase ${quarter} for ${goal.goal}`,
    dailyCommitment: '4-6 hours',
    weeklyTarget: `Complete Q${quarter} objectives`,
    specificGoals: [`Advance ${goal.goal} - Quarter ${quarter}`],
    dailyRoutine: createDefaultDailyRoutine(goal),
    weeklySchedule: [
      `Week 1: Foundation for ${goal.goal}`,
      `Week 2: Development of ${goal.goal}`,
      `Week 3: Implementation of ${goal.goal}`,
      `Week 4: Optimization of ${goal.goal}`
    ]
  });

  const createDefaultDailyRoutine = (goal) => ({
    warmUp: `Morning planning and setup for ${goal.goal}`,
    skillPractice: `Core work and skill development for ${goal.goal}`,
    conditioning: `Capability building for ${goal.goal}`,
    coolDown: `Review and documentation for ${goal.goal}`
  });

  const getDefaultQuarterTitle = (quarter) => {
    const titles = {
      1: 'Foundation & Planning',
      2: 'Development & Building', 
      3: 'Testing & Optimization',
      4: 'Launch & Scale'
    };
    return titles[quarter] || `Quarter ${quarter}`;
  };

  const getDefaultQuarterDescription = (quarter, goal) => {
    const descriptions = {
      1: `Establish foundation and planning framework for ${goal.goal}`,
      2: `Core development and building phase for ${goal.goal}`,
      3: `Testing, optimization and refinement of ${goal.goal}`,
      4: `Launch preparation and scaling strategies for ${goal.goal}`
    };
    return descriptions[quarter] || `Quarter ${quarter} focus on ${goal.goal}`;
  };

  const extractObjectivesFromPhase = (phase, quarter) => {
    if (phase.specificGoals && phase.specificGoals.length > 0) {
      return phase.specificGoals;
    }
    return [
      `Complete ${phase.title || `Quarter ${quarter}`} milestones`,
      `Achieve measurable progress in core areas`,
      `Maintain quality standards and timeline`
    ];
  };

  const calculatePriorityFromBusinessPhase = (dayOfWeek, businessPhase, weekNumber) => {
    // Monday planning and Wed-Thu core work are high priority
    if (dayOfWeek === 1 || dayOfWeek === 3 || dayOfWeek === 4) return 'HIGH';
    // Tuesday and Friday are medium priority
    if (dayOfWeek === 2 || dayOfWeek === 5) return 'MEDIUM';
    // Weekend is normal priority
    return 'NORMAL';
  };

  const extractMeasurableOutcomes = (businessPhase, dayOfWeek, weekNumber) => {
    const measurableSkills = businessPhase.measurableSkills || [];
    if (measurableSkills.length === 0) return [];
    
    return measurableSkills.map(skill => ({
      skill: skill.skill,
      targetForWeek: skill.week1Target || 'Baseline',
      measurementMethod: skill.measurementMethod || 'Self-assessment',
      dayFocus: dayOfWeek <= 4 ? 'Practice' : 'Review'
    }));
  };

  const getAssessmentCriteria = (businessPhase, dayOfWeek) => {
    const assessmentTests = businessPhase.assessmentTests || [];
    if (dayOfWeek === 5 && assessmentTests.length > 0) { // Friday assessment
      return assessmentTests[0] || 'Weekly progress assessment';
    }
    return null;
  };

  // Calculate current day since start
  const calculateDaysSinceStart = useCallback(() => {
    if (!stableFoundationPlan) return 1;
    const startDate = new Date(stableFoundationPlan.centralGoal.startDate);
    const today = new Date();
    const diffTime = Math.abs(today - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(1, diffDays);
  }, [stableFoundationPlan]);

  // Get current period info based on stable foundation
  const getCurrentPeriodInfo = useCallback(() => {
    if (!stableFoundationPlan) return { quarter: 1, week: 1, day: 1 };
    
    const daysSinceStart = calculateDaysSinceStart();
    const daysPerQuarter = stableFoundationPlan.timeline.daysPerQuarter;

    const currentQuarter = Math.min(4, Math.ceil(daysSinceStart / daysPerQuarter));
    const daysIntoQuarter = ((daysSinceStart - 1) % daysPerQuarter) + 1;
    const currentWeek = Math.min(Math.ceil(daysPerQuarter / 7), Math.ceil(daysIntoQuarter / 7));

    // Time-based quarter math is PACE, not progress. overallProgress is
    // completion-based and derived in the progress listener below.
    return {
      quarter: currentQuarter,
      week: currentWeek,
      day: daysSinceStart,
      quarterProgress: Math.min(100, (daysIntoQuarter / daysPerQuarter) * 100)
    };
  }, [stableFoundationPlan, calculateDaysSinceStart]);

  // Get today's plan from stable foundation
  const getTodaysPlan = useCallback(() => {
    if (!stableFoundationPlan) return null;
    
    const { quarter, week } = getCurrentPeriodInfo();
    const daysSinceStart = calculateDaysSinceStart();
    
    const currentQuarterData = stableFoundationPlan.quarters[quarter - 1];
    if (!currentQuarterData) return null;
    
    const currentWeekData = currentQuarterData.weeks[week - 1];
    if (!currentWeekData) return null;
    
    const dayInWeek = ((daysSinceStart - 1) % 7);
    const currentDayData = currentWeekData.days[dayInWeek];
    
    return {
      quarter: currentQuarterData,
      week: currentWeekData,
      day: currentDayData,
      actions: currentDayData?.actions || { primary: "Continue working on current objectives", secondary: [] },
      businessPhase: currentQuarterData.businessPhase,
      dailyRoutine: currentDayData?.businessRoutine,
      measurableOutcomes: currentDayData?.measurableOutcomes || []
    };
  }, [stableFoundationPlan, getCurrentPeriodInfo, calculateDaysSinceStart]);

  // Initialize with stable foundation plan
  useEffect(() => {
    console.log('useProgressTracking - received businessPlan:', businessPlan);
    console.log('useProgressTracking - businessPlan phases:', businessPlan?.phases);
    console.log('useProgressTracking - userGoal:', userGoal);
    
    if (!user || !userGoal || !businessPlan) {
      console.log('useProgressTracking - missing data:', { user: !!user, userGoal: !!userGoal, businessPlan: !!businessPlan });
      setLoading(false);
      return;
    }

    // Create stable foundation plan from business plan
    const foundationPlan = createStableFoundationPlan(userGoal, businessPlan);
    setStableFoundationPlan(foundationPlan);

    // Load progress data from Firebase
    // Create goal-specific storage paths to prevent mixing between different goals
    const goalId = userGoal?.id || userGoal?.goalId || 'default';
    const progressRef = ref(database, `users/${user.uid}/progress/${goalId}`);
    const dailyRef = ref(database, `users/${user.uid}/dailyHistory/${goalId}`);

    const unsubscribeProgress = onValue(progressRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const periodInfo = getCurrentPeriodInfo();
        const counts = deriveTaskCounts(data?.tasks);
        const pace = {
          day: calculateDaysSinceStart(),
          totalDays: foundationPlan?.centralGoal?.totalDays || stableFoundationPlan?.centralGoal?.totalDays || 0
        };
        pace.pct = pace.totalDays ? Math.min(100, (pace.day / pace.totalDays) * 100) : 0;
        setProgressData({
          ...data,
          ...periodInfo,
          ...counts,
          // Honest, completion-based progress — moves only when tasks complete.
          overallProgress: counts.totalTasks > 0 ? Math.min(100, (counts.completedTasks / counts.totalTasks) * 100) : 0,
          pace,
          daysSinceStart: pace.day
        });
      } else if (foundationPlan) {
        // Initialize with default data based on foundation plan
        const daysSinceStart = calculateDaysSinceStart();
        const periodInfo = getCurrentPeriodInfo();
        const defaultData = {
          overallProgress: 0,
          completedTasks: 0,
          totalTasks: 5,
          streakDays: Math.max(0, daysSinceStart - 1),
          timeSpent: 0,
          daysSinceStart: daysSinceStart,
          dailyGoalsMet: false,
          weeklyProgress: Math.min(100, ((daysSinceStart % 7) / 7) * 100),
          weeklyGoalsMet: false,
          ...periodInfo
        };
        setProgressData(defaultData);
        set(progressRef, defaultData);
      }
      setLoading(false);
    });

    const unsubscribeDaily = onValue(dailyRef, (snapshot) => {
      const data = snapshot.val() || {};
      setDailyHistory(data);
    });

    return () => {
      unsubscribeProgress();
      unsubscribeDaily();
    };
  }, [user, userGoal, businessPlan, createStableFoundationPlan]);

  // Clean up localStorage when goal changes to prevent mixing
  useEffect(() => {
    const currentGoalId = userGoal?.id || userGoal?.goalId;
    if (currentGoalId && user?.uid) {
      // Clean up any cached actions from other goals
      const allKeys = Object.keys(localStorage);
      allKeys.forEach(key => {
        if (key.startsWith(`aiActions_${user.uid}_`) && !key.includes(`_${currentGoalId}_`)) {
          console.log('Cleaning up cached action from different goal:', key);
          localStorage.removeItem(key);
        }
      });
    }
  }, [userGoal, user]);

  // Refresh progress data
  const refreshProgress = useCallback(async () => {
    if (!user || !stableFoundationPlan) return;
    
    setLoading(true);
    const daysSinceStart = calculateDaysSinceStart();
    const periodInfo = getCurrentPeriodInfo();
    const updatedData = {
      ...progressData,
      ...periodInfo,
      daysSinceStart: daysSinceStart,
      // Completion-based, never time-elapsed.
      overallProgress: (progressData.totalTasks > 0)
        ? Math.min(100, (progressData.completedTasks / progressData.totalTasks) * 100)
        : 0,
      weeklyProgress: Math.min(100, ((daysSinceStart % 7) / 7) * 100),
      streakDays: Math.max(0, daysSinceStart - 1)
    };
    
    try {
      const goalId = userGoal?.id || userGoal?.goalId || 'default';
      await update(ref(database, `users/${user.uid}/progress/${goalId}`), updatedData);
      setProgressData(updatedData);
    } catch (error) {
      console.error('Error refreshing progress:', error);
    } finally {
      setLoading(false);
    }
  }, [user, stableFoundationPlan, progressData, getCurrentPeriodInfo, calculateDaysSinceStart]);

  // Get week completion data
  const getWeekCompletionData = useCallback((weekOffset = 0) => {
    const weekData = [];
    const today = new Date();
    
    // Calculate the target date based on offset
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + (weekOffset * 7));
    
    // Start from Monday of the target week
    const mondayDate = new Date(targetDate);
    const dayOfWeek = targetDate.getDay();
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    mondayDate.setDate(targetDate.getDate() - daysToMonday);

    // Always generate exactly 7 days (Monday to Sunday)
    for (let i = 0; i < 7; i++) {
      const date = new Date(mondayDate);
      date.setDate(mondayDate.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      const dayData = dailyHistory[dateStr] || { completionPercent: 0 };
      
      weekData.push({
        date: dateStr,
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' })[0],
        fullDayName: date.toLocaleDateString('en-US', { weekday: 'long' }),
        completionPercent: dayData.completionPercent,
        isToday: dateStr === new Date().toISOString().split('T')[0],
        isFuture: date > today
      });
    }

    return weekData;
  }, [dailyHistory]);

  // Get today's stored daily action from localStorage or generate new one
  const getTodaysStoredAction = useCallback(() => {
    if (!user || !stableFoundationPlan) return null;
    
    const today = new Date().toISOString().split('T')[0];
    const goalId = userGoal?.id || userGoal?.goalId || 'default';
    const cacheKey = `aiActions_${user.uid}_${goalId}_${today}`;
    
    try {
      const stored = localStorage.getItem(cacheKey);
      if (stored) {
        const parsedAction = JSON.parse(stored);
        // Check if it's from today and valid
        if (parsedAction.date === today && parsedAction.actions) {
          return parsedAction.actions;
        }
      }
    } catch (error) {
      console.error('Error reading stored daily action:', error);
    }
    
    return null;
  }, [user, stableFoundationPlan]);

  // Store today's daily action in localStorage
  const storeTodaysAction = useCallback((actionData) => {
    if (!user) return;
    
    const today = new Date().toISOString().split('T')[0];
    const goalId = userGoal?.id || userGoal?.goalId || 'default';
    const cacheKey = `aiActions_${user.uid}_${goalId}_${today}`;
    
    const dataToStore = {
      actions: actionData,
      generatedAt: Date.now(),
      date: today
    };
    
    try {
      localStorage.setItem(cacheKey, JSON.stringify(dataToStore));
    } catch (error) {
      console.error('Error storing daily action:', error);
    }
  }, [user]);

  // Business Phase Logic
  const getBusinessPhaseFromProgress = (overallProgress) => {
    if (overallProgress < 30) return 'Foundation';
    if (overallProgress < 70) return 'Growth';
    return 'Scale';
  };

  const getPhaseObjective = (phase) => {
    const objectives = {
      'Foundation': 'Building core systems, validating market, establishing basics',
      'Growth': 'Scaling operations, increasing revenue, expanding market reach',
      'Scale': 'Optimizing systems, maximizing efficiency, preparing for next level'
    };
    return objectives[phase] || objectives['Foundation'];
  };

  const getPhaseSpecificDayFocus = (dayName, phase) => {
    const dayFocus = {
      'Foundation': {
        'Monday': 'System Setup Planning',
        'Tuesday': 'Core Skill Development',
        'Wednesday': 'Market Research & Validation',
        'Thursday': 'Foundation Building',
        'Friday': 'Progress Review & Planning',
        'Saturday': 'Strategic Learning',
        'Sunday': 'Vision & Goal Alignment'
      },
      'Growth': {
        'Monday': 'Revenue Strategy Planning',
        'Tuesday': 'Customer Acquisition',
        'Wednesday': 'Product/Service Delivery',
        'Thursday': 'Business Development',
        'Friday': 'Performance Analysis',
        'Saturday': 'Market Expansion',
        'Sunday': 'Strategic Growth Planning'
      },
      'Scale': {
        'Monday': 'Optimization Strategy',
        'Tuesday': 'System Automation',
        'Wednesday': 'Team/Process Scaling',
        'Thursday': 'Efficiency Maximization',
        'Friday': 'Performance Optimization',
        'Saturday': 'Innovation & R&D',
        'Sunday': 'Vision & Strategy Refinement'
      }
    };
    return dayFocus[phase]?.[dayName] || 'Business Development';
  };

  const getPhaseTimeline = (phase, progress) => {
    const timelines = {
      'Foundation': `Foundation Phase (Days 1-30, currently ${progress.toFixed(1)}% through overall plan)`,
      'Growth': `Growth Phase (Days 31-70, currently ${progress.toFixed(1)}% through overall plan)`,
      'Scale': `Scale Phase (Days 71-90, currently ${progress.toFixed(1)}% through overall plan)`
    };
    return timelines[phase] || timelines['Foundation'];
  };

  const getPhaseCompletion = (overallProgress) => {
    if (overallProgress < 30) return ((overallProgress / 30) * 100).toFixed(1);
    if (overallProgress < 70) return (((overallProgress - 30) / 40) * 100).toFixed(1);
    return (((overallProgress - 70) / 30) * 100).toFixed(1);
  };

  const getRevenueActionCount = (progressData) => {
    // This would ideally track revenue-specific actions
    return Math.floor((progressData.completedTasks || 0) * 0.3);
  };

  const getSystemBuildingProgress = (progressData) => {
    // This would ideally track system-building specific progress
    return Math.min(100, (progressData.overallProgress || 0) * 1.2);
  };

  const getPhaseSpecificTaskRules = (phase) => {
    const rules = {
      'Foundation': `**FOUNDATION PHASE FOCUS:**
- Build core business systems and processes
- Validate market demand and business model
- Establish foundational skills and knowledge
- Create minimum viable product/service
- Set up essential business infrastructure
**Today's task should focus on building something fundamental that everything else depends on.**`,
      'Growth': `**GROWTH PHASE FOCUS:**
- Scale revenue-generating activities
- Expand market reach and customer base
- Optimize existing systems for efficiency
- Develop marketing and sales processes
- Build team and operational capacity
**Today's task should focus on activities that directly increase revenue or market reach.**`,
      'Scale': `**SCALE PHASE FOCUS:**
- Automate and systematize operations
- Maximize efficiency and profitability
- Expand into new markets or products
- Build sustainable competitive advantages
- Prepare for next level of growth
**Today's task should focus on optimization, automation, or strategic expansion.**`
    };
    return rules[phase] || rules['Foundation'];
  };

  // Generate AI-enhanced daily actions based on business plan context
  const generateAIEnhancedDailyActions = useCallback(async (journalContext = '', progressContext = {}) => {
    if (!stableFoundationPlan) return null;

    // Check if we already have today's action stored
    const storedAction = getTodaysStoredAction();
    if (storedAction) {
      console.log('Using stored daily action for today');
      return storedAction;
    }

    const todaysPlan = getTodaysPlan();
    const currentPeriod = getCurrentPeriodInfo();
    const dayOfWeek = ((calculateDaysSinceStart() - 1) % 7) + 1;
    const dayName = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][dayOfWeek - 1];
    // Timeline-based pace drives phase math; completion-based overallProgress is displayed.
    const pace = progressData.pace || {
      day: calculateDaysSinceStart(),
      totalDays: stableFoundationPlan?.centralGoal?.totalDays || 0
    };

    console.log('Generating new AI-enhanced daily action for today');

    // Rate limiting: Add 3-second delay
    await new Promise(resolve => setTimeout(resolve, 3000));

    try {
      const data = await aiChat({
        task: 'daily-action',
        messages: [
            {
              role: "system",
              content: `# AI Business Plan Daily Task Generator

You are an expert business strategist specializing in turning comprehensive business plans into daily actions. Your job is to generate 1 primary action for today that directly advances their business plan phase.

## BUSINESS CONTEXT:
**Goal**: ${stableFoundationPlan.centralGoal.title}
**Goal Description**: ${stableFoundationPlan.centralGoal.description}
**Current Business Phase**: ${getBusinessPhaseFromProgress(progressData.pace?.pct ?? 0)}
**Phase Objective**: ${getPhaseObjective(getBusinessPhaseFromProgress(progressData.pace?.pct ?? 0))}
**Day**: ${calculateDaysSinceStart()} of ${stableFoundationPlan.centralGoal.totalDays}
**Business Progress**: ${progressData.overallProgress}% of tasks complete (day ${pace.day} of ${pace.totalDays})
**Day of Week**: ${dayName} (${getPhaseSpecificDayFocus(dayName, getBusinessPhaseFromProgress(progressData.pace?.pct ?? 0))})
**Phase Timeline**: ${getPhaseTimeline(getBusinessPhaseFromProgress(progressData.pace?.pct ?? 0), progressData.pace?.pct ?? 0)}

## BUSINESS PLAN PROGRESS:
- Phase Completion: ${getPhaseCompletion(progressData.pace?.pct ?? 0)}%
- Completed Business Actions: ${progressContext.completedTasks || progressData.completedTasks}
- Business Development Streak: ${progressContext.streakDays || progressData.streakDays} days
- Weekly Business Progress: ${progressData.weeklyProgress}%
- Revenue Actions Completed: ${getRevenueActionCount(progressData)}
- System Building Progress: ${getSystemBuildingProgress(progressData)}%
- Daily Goals Met: ${progressData.dailyGoalsMet}

## JOURNAL CONTEXT:
${journalContext || 'No recent journal entries'}

## PHASE-SPECIFIC TASK GENERATION:
${getPhaseSpecificTaskRules(getBusinessPhaseFromProgress(progressData.pace?.pct ?? 0))}

## UNIVERSAL TASK RULES:
1. **Business Impact**: Every task must directly advance business development
2. **Time-bound**: Completable in 4-6 hours of focused work
3. **Measurable**: Include specific success criteria and deliverables
4. **Phase-aligned**: Must match the current business phase objectives
5. **Revenue-focused**: Consider how this action contributes to revenue generation

## RESPONSE FORMAT:
Return ONLY a JSON object with this exact structure:
{
  "baseAction": "Specific ${getBusinessPhaseFromProgress(progressData.pace?.pct ?? 0)} phase task for today",
  "context": {
    "priority": "HIGH|MEDIUM|LOW",
    "estimatedHours": "4-6h",
    "businessPhase": "${getBusinessPhaseFromProgress(progressData.pace?.pct ?? 0)}",
    "phaseProgress": "${getPhaseCompletion(progressData.pace?.pct ?? 0)}%",
    "quarter": ${currentPeriod.quarter},
    "week": ${currentPeriod.week},
    "dayOfWeek": ${dayOfWeek},
    "successCriteria": "Specific measurable business outcome",
    "skillsFocused": ["business skill 1", "business skill 2"],
    "revenueImpact": "How this directly contributes to revenue/business growth"
  }
}`
            },
            {
              role: "user",
              content: `Generate today's primary action for achieving: "${stableFoundationPlan.centralGoal.title}". Consider my current progress, the day of the week, and any journal context to create a smart, specific task that moves me forward strategically.`
            }
        ],
        temperature: 0.7,
        max_tokens: 500
      });

      if (data.choices && data.choices[0]) {
        try {
          const aiResponse = JSON.parse(data.choices[0].message.content);
          // Store the generated action for today
          storeTodaysAction(aiResponse);
          console.log('Generated and stored new daily action:', aiResponse.baseAction);
          return aiResponse;
        } catch (parseError) {
          console.error('Error parsing AI response:', parseError);
          // Fallback to basic action
          const fallback = getFallbackAction(todaysPlan, dayOfWeek, dayName);
          storeTodaysAction(fallback);
          return fallback;
        }
      }
    } catch (error) {
      console.error('Error generating AI-enhanced daily actions:', error);
      // Return fallback action
      const fallback = getFallbackAction(todaysPlan, dayOfWeek, dayName);
      storeTodaysAction(fallback);
      return fallback;
    }

    const fallback = getFallbackAction(todaysPlan, dayOfWeek, dayName);
    storeTodaysAction(fallback);
    return fallback;
  }, [stableFoundationPlan, getTodaysPlan, getCurrentPeriodInfo, calculateDaysSinceStart, progressData, getTodaysStoredAction, storeTodaysAction]);

  // Fallback action generator for when AI fails
  const getFallbackAction = (todaysPlan, dayOfWeek, dayName) => {
    const goalTitle = stableFoundationPlan?.centralGoal?.title || 'your goal';
    const phase = todaysPlan?.businessPhase?.title || 'Current Phase';
    
    return {
      baseAction: `Continue development work on ${goalTitle} - Focus on ${phase} implementation`,
      context: {
        priority: 'HIGH',
        estimatedHours: '4-6h',
        quarter: getCurrentPeriodInfo().quarter,
        week: getCurrentPeriodInfo().week,
        dayOfWeek: dayOfWeek,
        successCriteria: 'Complete key milestone for today',
        skillsFocused: ['development', 'implementation']
      }
    };
  };

  // Get contextual daily actions (enhanced with AI)
  const getContextualDailyActions = useCallback((journalEntry = null) => {
    if (!stableFoundationPlan) return [];

    // First try to get stored AI action for today
    const storedAction = getTodaysStoredAction();
    if (storedAction) {
      return [{
        id: `stored-${calculateDaysSinceStart()}-primary`,
        action: storedAction.baseAction,
        priority: storedAction.context?.priority || 'HIGH',
        timeEstimate: storedAction.context?.estimatedHours || '4-6h',
        category: 'primary',
        day: storedAction.context?.dayOfWeek || ((calculateDaysSinceStart() - 1) % 7) + 1,
        dayName: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][((calculateDaysSinceStart() - 1) % 7)],
        measurable: true,
        businessPhase: getTodaysPlan()?.businessPhase?.title || 'Foundation Phase',
        dailyRoutine: getTodaysPlan()?.dailyRoutine,
        measurableOutcomes: getTodaysPlan()?.day?.measurableOutcomes || [],
        successCriteria: storedAction.context?.successCriteria,
        skillsFocused: storedAction.context?.skillsFocused || []
      }];
    }

    // Fallback to foundation plan actions
    const todaysPlan = getTodaysPlan();
    if (!todaysPlan || !todaysPlan.day) return [];

    const dayActions = todaysPlan.day.actions;
    const businessPhase = todaysPlan.businessPhase;
    const dayOfWeek = ((calculateDaysSinceStart() - 1) % 7) + 1;

    const actions = [{
      id: `foundation-${calculateDaysSinceStart()}-primary`,
      action: dayActions.primary,
      priority: todaysPlan.day.priority || 'HIGH',
      timeEstimate: dayActions.timeCommitment || '4-6h',
      category: 'primary',
      day: dayOfWeek,
      dayName: todaysPlan.day.dayName,
      measurable: true,
      businessPhase: businessPhase.title,
      dailyRoutine: todaysPlan.dailyRoutine,
      measurableOutcomes: todaysPlan.day.measurableOutcomes
    }];

    // Add secondary actions
    if (dayActions.secondary && dayActions.secondary.length > 0) {
      dayActions.secondary.forEach((secondaryAction, index) => {
        actions.push({
          id: `foundation-${calculateDaysSinceStart()}-secondary-${index}`,
          action: secondaryAction,
          priority: 'MEDIUM',
          timeEstimate: '30min-1h',
          category: 'secondary',
          day: dayOfWeek,
          dayName: todaysPlan.day.dayName,
          measurable: false
        });
      });
    }

    return actions;
  }, [stableFoundationPlan, getTodaysPlan, calculateDaysSinceStart, getTodaysStoredAction]);

  return {
    progressData,
    dailyHistory,
    hierarchicalPlan: stableFoundationPlan, // Use stable foundation as hierarchical plan
    loading,
    getTodaysPlan,
    getCurrentPeriodInfo,
    getWeekCompletionData,
    refreshProgress,
    getContextualDailyActions,
    generateAIEnhancedDailyActions, // NEW: AI-powered daily task generation
    getTodaysStoredAction, // NEW: Get today's stored action from localStorage
    storeTodaysAction, // NEW: Store today's action in localStorage
    // New business foundation specific methods
    getBusinessFoundation: () => stableFoundationPlan?.businessFoundation,
    getCurrentBusinessPhase: () => {
      const { quarter } = getCurrentPeriodInfo();
      return stableFoundationPlan?.quarters[quarter - 1]?.businessPhase;
    },
    getMeasurableOutcomes: () => getTodaysPlan()?.measurableOutcomes || []
  };
};

export default useProgressTracking;